import * as React from 'react';

import * as Images from 'pos/Images';
import * as UI from 'core/ui';

import { Log, Level } from 'core/infrastructure';
import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize } from 'pos/ui';
import { Res } from 'core/api';
import { Cordova } from 'pos/common';
import { EnumUtils } from 'core/utils';
import { SettingsProvider } from 'pos/services';
import { Flex, Cols, Col } from 'core/ui';
import { Message, Modal } from 'core/ui-next/modal';

type State = {
	logType: 'web' | 'cordova';
	logContent: string;
	logLevel: Level;
};

class LogScreen extends PosScreenClassComponent<Modal.Actions<void>, State> {

	createInitialState(_props: Modal.Actions<void>): Nullable<State> {
		return {
			logType: 'web',
			logContent: Log.getEntries().join('\r\n'),
			logLevel: SettingsProvider.load().logLevel
		};
	}

	protected getScreenId(): string {
		return 'SHOW_LOG';
	}

	protected getTitle(): string {
		return Res.Web.LOG_VIEW();
	}

	protected getImage(): string | SvgIcon {
		return Images.Log;
	}

	protected getSize(): ScreenSize {
		return 'full-screen';
	}

	protected useKeypad() {
		return false;
	}

	private async uploadCordovaLogs(): Promise<void> {
		try {
			await Cordova.uploadFullLog(SettingsProvider.load());
			await Message.show(Res.Msg.MSG_CORDOVA_LOGS_UPLOADED());
		}
		catch (error: any) {
			await Message.show(Res.Msg.MSG_CORDOVA_LOGS_UPLOAD_FAILED(error.message || error.msg || error));
		}
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {

		return [
			{ title: Res.Common.CANCEL_BTN(), image: Images.Cancel, action: async () => this.cancel() },
			null,
			null,
			null,
			null,
			Cordova.isCordovaNavigator() && this.state.logType === 'cordova' ? {
				title: Res.Web.LOG_VIEW_UPLOAD_LOGS_BTN(), image: Images.UploadLog, action: () => this.uploadCordovaLogs()
			} : null
		];
	}

	onComponentDidMount(): void { }
	onComponentWillUnmount(): void { }

	protected getContent(): React.ReactNode {

		const { Form, ComboBox } = UI.controlsFor<State>();
		const { Button } = UI;

		const logLevels = EnumUtils.getValues(Level);

		const scrollToBottom = (ref: any) => {
			if (ref)
				ref.scrollTop = ref.scrollHeight;
		};

		const setWebLogContent = () => {
			const logContent = Log.getEntries().join('\r\n');
			this.setState({ logContent, logType: 'web' });
		};

		const setCordovaLogContent = async () => {
			const logContent = await Cordova.getLogContent();
			this.setState({ logContent, logType: 'cordova' });
		};

		const onLevelChange = async (values: any[]) => {
			const oldSettings = SettingsProvider.load();
			SettingsProvider.save({
				...oldSettings,
				logLevel: values[0]
			});
			Log.setLevel(values[0]);

			if (Cordova.isCordovaNavigator()) {
				void Cordova.setLogLevel(values[0]);
			}

			this.setState({ logLevel: values[0] });
		};

		return <Flex.Vertical>
			<Flex.Item>
				<Form model={this.state}>
					<Cols style={{ justifyContent: 'center' }} className='m-t-sm'>
						<Col className='m-t-xs m-r-xs text-16'>
							{Res.Web.POS_CONFIG_LOG_LEVEL_LBL()}
						</Col>
						<Col>
							<ComboBox options={logLevels} format={x => EnumUtils.getName(Level, x)} linkTo={x => x.logLevel} onValueChanged={onLevelChange} required />
						</Col>
					</Cols>
				</Form>
			</Flex.Item>
			<Flex.Item>
				{Cordova.isCordovaNavigator() ?
					<Cols>
						<Col width='*' className='m-r-sm'>
							<Button buttonStyle={this.state.logType === 'web' ? 'info' : 'default'} className='btn-block' onClick={setWebLogContent}>{Res.Web.LOG_VIEW_WEB_LOGS_BTN()}</Button>
						</Col>
						<Col width='*' className='m-l-sm'>
							<Button buttonStyle={this.state.logType === 'cordova' ? 'info' : 'default'} className='btn-block' onClick={setCordovaLogContent}>{Res.Web.LOG_VIEW_WEB_NATIVE_LOGS_BTN()}</Button>
						</Col>
					</Cols>
					: null}
			</Flex.Item>
			<Flex.Item size='*' className='border-top'>
				<pre ref={scrollToBottom} style={{ marginTop: 5, marginBottom: 5, border: 0 }}>
					{this.state.logContent}
				</pre>
			</Flex.Item>
		</Flex.Vertical>;
	}
}

export default async function showLog(): Promise<Result> {
	return Modal.show(LogScreen);
}
